import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import QuestionsSection from '../components/QuestionsSection';
import { 
  ArrowRight, 
  Shield, 
  Eye, 
  Clock, 
  FileCheck, 
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  ExternalLink
} from 'lucide-react';

const LandingPagePro = () => {
  const [activePhilosophy, setActivePhilosophy] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const philosophies = [
    {
      title: "Pattern Recognition",
      subtitle: "A decade of watching companies succeed and fail",
      description: "Over ten years, we've observed what separates companies that thrive from those that struggle. The difference rarely lies in the business model—it lies in the foundation. Clean books, compliant structures, and documented processes aren't bureaucracy. They're insurance against the future you can't predict.",
      icon: Eye
    },
    {
      title: "Investor-Ready From Day One",
      subtitle: "We know what due diligence uncovers",
      description: "Investors and acquirers don't just evaluate your revenue—they scrutinize your cap table, your compliance history, your board minutes from three years ago. We've seen deals delayed or killed because of sloppy incorporation paperwork. We build your foundation as if due diligence is happening tomorrow.",
      icon: TrendingUp
    },
    {
      title: "Security as Standard",
      subtitle: "ISO 27001-certified framework",
      description: "We operate under an ISO 27001–certified information security framework with strict account-based access controls, encrypted storage environments, and monitored audit trails. All sensitive documentation is retained only as operationally necessary and is permanently deleted within seven (7) days of matter completion in accordance with our automated data-purge protocol.",
      icon: Shield
    },
    {
      title: "Documented Everything",
      subtitle: "Auditable processes, not tribal knowledge",
      description: "Every filing, every communication, every decision point is documented and timestamped. When you need to demonstrate what happened and when, you have a complete audit trail. No hunting through emails. No relying on someone's memory. This is how institutional clients expect their advisors to work.",
      icon: FileCheck
    }
  ];

  const indiaServices = [
    {
      title: "Private Limited Company",
      timeline: "~15 days",
      description: "End-to-end incorporation with DSC, DIN, name approval, and MCA filing. Includes PAN, TAN, and bank account assistance."
    },
    {
      title: "Limited Liability Partnership",
      timeline: "~12 days",
      description: "LLP formation with DPIN, name reservation, and incorporation certificate. Ideal for professional services and consulting firms."
    },
    {
      title: "One Person Company",
      timeline: "~15 days",
      description: "Single promoter company with limited liability protection. Complete incorporation with nominee director arrangement."
    },
    {
      title: "Annual ROC Compliance",
      timeline: "Yearly",
      description: "AOC-4, MGT-7, and all mandatory annual filings. Board meeting minutes, statutory registers, and director KYC maintenance."
    },
    {
      title: "Statutory Audit",
      timeline: "Yearly",
      description: "Complete statutory audit under Companies Act with audit report, CARO compliance, and financial statement certification."
    },
    {
      title: "GST Registration & Filing",
      timeline: "Monthly/Quarterly",
      description: "GST registration, monthly/quarterly returns (GSTR-1, GSTR-3B), annual returns, and input tax credit reconciliation."
    },
    {
      title: "Income Tax & TDS",
      timeline: "Quarterly/Yearly",
      description: "Corporate tax planning, advance tax computation, TDS compliance, and annual return filing with tax optimization."
    },
    {
      title: "Payroll & PF/ESIC",
      timeline: "Monthly",
      description: "CTC structuring, salary processing, PF/ESIC registration and monthly compliance, professional tax, and Form 16."
    },
    {
      title: "FEMA & RBI Compliance",
      timeline: "As needed",
      description: "FDI/ODI compliance, FC-GPR/FC-TRS filings, ECB reporting, and cross-border transaction advisory."
    },
    {
      title: "Startup Recognition (DPIIT)",
      timeline: "~7 days",
      description: "DPIIT startup recognition, tax exemption certificates (80-IAC), angel tax exemption, and seed fund applications."
    },
    {
      title: "Trademark Registration",
      timeline: "~18 months",
      description: "Trademark search, application filing, examination response, and registration certificate. Opposition handling included."
    },
    {
      title: "ESOP Structuring",
      timeline: "Project-based",
      description: "Employee stock option plan design, valuation, board resolutions, grant letters, and Companies Act compliance."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0b1310] text-white antialiased">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-[#0f1611]/95 backdrop-blur-md border-b border-emerald-900/40 shadow-lg shadow-black/30' : 'bg-[#0f1611]/70 backdrop-blur-sm'
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Snigdha & Associates" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <p className="text-sm font-medium text-white">Snigdha & Associates</p>
                <p className="text-xs text-slate-400">Chartered Accountants</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <a href="#philosophy" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors hidden md:block">Philosophy</a>
              <a href="#services" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors hidden md:block">India</a>
              <a href="#contact" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors hidden md:block">Contact</a>
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 h-auto">
                  Client Portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-8">
            <Shield className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs text-white/80 font-medium">ISO 27001 Certified Practice</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mb-6 leading-[1.1] text-white">
            The foundation<br />
            <span className="text-slate-400">determines the structure</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
            A decade of building compliant, investor-ready companies has taught us one thing: 
            the work you do today shapes the options you have tomorrow.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login">
              <Button className="bg-emerald-500 text-white hover:bg-emerald-600 px-8 py-6 text-base font-medium">
                Start a Conversation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#philosophy">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10 px-8 py-6 text-base border border-emerald-800/50">
                Our Philosophy
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          <a
            href="#mp-startup-promo"
            data-testid="hero-mp-startup-explore-link"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('mp-startup-promo')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-1.5 mt-8 px-3 py-1.5 rounded-full bg-emerald-500/100/10 border border-emerald-500/30 text-xs text-emerald-300 hover:bg-emerald-500/100/20 hover:text-emerald-200 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            New: MP Startup Funding &amp; Benefits — explore
            <ChevronDown className="w-3 h-3" />
          </a>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-24 md:py-32 px-6 bg-[#0f1611]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-sm text-emerald-400 uppercase tracking-widest mb-4 font-medium">Our Philosophy</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              What we've learned in<br />
              <span className="text-slate-400">ten years of practice</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            {/* Philosophy Navigation */}
            <div className="space-y-2">
              {philosophies.map((phil, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhilosophy(i)}
                  className={`w-full text-left p-6 rounded-lg transition-all duration-300 ${
                    activePhilosophy === i 
                      ? 'bg-[#152219] border border-emerald-900/40' 
                      : 'hover:bg-[#142019] border border-transparent'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded flex items-center justify-center transition-colors ${
                      activePhilosophy === i ? 'bg-emerald-500/100/20' : 'bg-[#152219]'
                    }`}>
                      <phil.icon className={`w-5 h-5 ${activePhilosophy === i ? 'text-emerald-400' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className={`font-medium mb-1 transition-colors ${
                        activePhilosophy === i ? 'text-white' : 'text-slate-300'
                      }`}>
                        {phil.title}
                      </p>
                      <p className="text-sm text-slate-400">{phil.subtitle}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Philosophy Content */}
            <div className="flex items-center">
              <div className="p-8 bg-[#0b1310] rounded-xl border border-emerald-900/40">
                <p className="text-lg md:text-xl text-slate-300 leading-relaxed font-light">
                  {philosophies[activePhilosophy].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - India */}
      <section id="services" className="py-24 md:py-32 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <p className="text-sm text-slate-400 uppercase tracking-widest">India</p>
              <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">IN</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-light mb-4 text-white">
              Full-spectrum corporate<br />
              <span className="text-slate-400">and tax advisory</span>
            </h2>
            <p className="text-slate-400 max-w-xl">
              From incorporation to exit, we provide the compliance infrastructure 
              that lets you focus on building your business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {indiaServices.map((service, i) => (
              <div key={i} className="group p-6 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-orange-500/50 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-medium">{service.title}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-orange-500/10 text-orange-400 whitespace-nowrap">
                    {service.timeline}
                  </span>
                </div>
                <p className="text-sm text-white/50 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 p-6 rounded-xl bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-medium mb-1">Ready to incorporate in India?</h3>
                <p className="text-sm text-white/50">Get started with a free consultation on entity structure.</p>
              </div>
              <Link to="/login">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6">
                  Start India Setup
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Questions Section - India */}
            <div className="mt-16" id="questions-india">
              <QuestionsSection darkMode={true} region="IN" />
            </div>
        </div>
      </section>

      {/* MP Startup Benefits Section */}
      <section id="mp-startup-promo" className="py-24 md:py-32 px-6 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <p className="text-sm text-slate-400 uppercase tracking-widest">Madhya Pradesh</p>
              <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/100/20 text-emerald-400 border border-emerald-500/30">MP</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-light mb-4 text-white">
              MP Startup funding<br />
              <span className="text-slate-400">and benefits</span>
            </h2>
            <p className="text-slate-400 max-w-xl">
              Under the MP Startup Policy 2025, three registrations unlock every grant,
              tax exemption, and reimbursement on offer — up to ₹30 lakh seed fund,
              ₹20 lakh patent assistance, and 15 schemes in total.
            </p>
          </div>

          <Link to="/mp-startup-benefits" data-testid="mp-startup-promo-card" className="block group">
            <div className="rounded-2xl bg-slate-800/50 border border-slate-700 group-hover:border-emerald-500/60 transition-colors p-8 md:p-12 overflow-hidden">
              <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6">
                    <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs text-white/80 font-medium">MP Startup Policy 2025</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-white mb-4 leading-tight">
                    Madhya Pradesh owes<br />
                    <span className="text-slate-400">your startup money.</span>
                  </h3>
                  <p className="text-slate-400 max-w-xl mb-8 font-light">
                    Company or LLP. Startup India recognition. MP Startup registration.
                    Each rung unlocks what the last one didn't. See where you stand
                    and what you qualify for.
                  </p>
                  <Button className="bg-emerald-500 text-white hover:bg-emerald-600 px-6 py-5 text-base font-medium pointer-events-none">
                    Explore MP Startup Benefits
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
                <div className="hidden lg:block">
                  <div className="relative pl-2">
                    <div className="absolute left-[1.55rem] top-6 bottom-6 w-px bg-gradient-to-b from-emerald-400/60 via-emerald-400/30 to-slate-600" />
                    {[
                      { n: 3, title: 'MP Startup registration', sub: 'The cash starts here' },
                      { n: 2, title: 'Startup India (DPIIT)', sub: 'Six protections unlock here' },
                      { n: 1, title: 'Company or LLP', sub: 'The entry ticket' }
                    ].map((s) => (
                      <div key={s.n} className="relative flex items-center gap-4 py-3">
                        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium border ${
                          s.n === 3 ? 'bg-emerald-500/100 border-emerald-400 text-white shadow-lg shadow-emerald-500/25' : 'bg-slate-800 border-slate-600 text-emerald-400'
                        }`}>
                          {s.n}
                        </div>
                        <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700 flex-1">
                          <p className="text-white text-sm font-medium">{s.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Questions Section - MP */}
          <div className="mt-16" id="questions-mp">
            <QuestionsSection darkMode={true} region="MP" />
          </div>
        </div>
      </section>

      {/* Founding Partners & Team Section */}
      {/* About Section */}
      <section id="about" className="py-24 md:py-32 px-6 bg-[#0b1310]">
        <div className="max-w-6xl mx-auto">
          {/* Founding Partners */}
          <div className="mb-20">
            <p className="text-sm text-emerald-400 uppercase tracking-widest mb-4 font-medium">Leadership</p>
            <h2 className="text-3xl md:text-4xl font-light mb-4 text-white">
              Founding <span className="text-slate-400">Partners</span>
            </h2>
            <p className="text-slate-400 max-w-xl mb-12">
              Two decades of combined expertise in cross-border compliance, taxation, and startup advisory.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* CA Sumit Nigam */}
              <div className="group p-6 rounded-2xl bg-[#0b1310] border border-emerald-900/40 hover:border-emerald-800/50 hover:shadow-lg transition-all">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-xl overflow-hidden flex-shrink-0 mx-auto sm:mx-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_8e73aa97-f2ea-4b76-8873-5ae0fbceef23/artifacts/x1k1a9c4_cf5217a7-1073-4e5e-9892-e0f45e7fec1a.jpg"
                      alt="CA Sumit Nigam"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl font-medium text-white mb-1">CA Sumit Nigam</h3>
                    <p className="text-emerald-400 text-sm mb-4">Founding Partner</p>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      10+ years of experience in Accounting across India and US regulatory frameworks. 
                      Specializes in cross-border financial compliance and corporate structuring.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
                      <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-600">India</span>
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-600">US</span>
                      <span className="text-xs px-2 py-1 rounded bg-[#152219] text-slate-400">Accounting</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CA Snigdha Nigam */}
              <div className="group p-6 rounded-2xl bg-[#0b1310] border border-emerald-900/40 hover:border-emerald-800/50 hover:shadow-lg transition-all">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-xl overflow-hidden flex-shrink-0 mx-auto sm:mx-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/i9349frw_Snigdha%20Ji.jpg"
                      alt="CA Snigdha Nigam"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl font-medium text-white mb-1">CA Snigdha Nigam</h3>
                    <p className="text-emerald-400 text-sm mb-4">Founding Partner</p>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      10+ years of experience in Startup Consultancy, US Taxation, State & Federal Filings, 
                      ODI & FEMA Compliance. Expert in cross-border regulatory matters.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-600">US Tax</span>
                      <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-600">FEMA</span>
                      <span className="text-xs px-2 py-1 rounded bg-[#152219] text-slate-400">Startups</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Office Photos - India */}
          <div className="mb-16">
            <p className="text-sm text-emerald-400 uppercase tracking-widest mb-4 font-medium">Our Office in India</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/joytxbul_2025-01-26.webp"
                  alt="Team meeting"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/j01nv5l2_2025-12-03.webp"
                  alt="Office workspace"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/6g9ffje9_2025-07-18.webp"
                  alt="Team collaboration"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/1b7jg8y4_2024-02-14.webp"
                  alt="Office environment"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
          
          {/* Office Photos - US */}
          <div>
            <p className="text-sm text-emerald-400 uppercase tracking-widest mb-4 font-medium">Our Office in US</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/36hegbf4_we-work-domain.jpg"
                  alt="US Office"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/dcr7xfst_business-model-of-we-work-5cb6e35ba767dbcb15c02a65.png"
                  alt="US Workspace"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/otcec6qw_wework-devonshire-1.jpg"
                  alt="US Meeting Room"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="relative group overflow-hidden rounded-xl aspect-[4/3]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_filingstatus/artifacts/xypblipb_Web_150DPI-20170915_Selling_Offices_Online_-_12_Person_Window-2_Select.avif"
                  alt="US Office Space"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-24 md:py-32 px-6 bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm text-slate-400 uppercase tracking-widest mb-4">Why It Matters</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              Economical is not the same<br />
              <span className="text-slate-400">as cheap</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl bg-slate-800/50 border border-slate-700">
              <Clock className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-white font-medium mb-2">Responsive</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Questions answered within hours, not days. You speak directly with qualified professionals, 
                not support representatives reading from scripts.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-slate-800/50 border border-slate-700">
              <FileCheck className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-white font-medium mb-2">Systematic</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Every deadline tracked, every document logged, every communication recorded. 
                You'll never wonder what happened or when.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-slate-800/50 border border-slate-700">
              <Shield className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-white font-medium mb-2">Secure</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                ISO 27001 certified with automatic 7-day data deletion post-engagement. 
                Your sensitive documents don't live on our servers indefinitely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 bg-emerald-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light mb-6 text-white">
            Ready to build on<br />
            <span className="text-emerald-200">solid ground?</span>
          </h2>
          <p className="text-emerald-100 mb-8 max-w-xl mx-auto">
            Whether you're incorporating a new entity or restructuring an existing one, 
            we'll help you build a foundation that serves your future.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login">
              <Button className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-base font-medium">
                Access Client Portal
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:hi@snigdha360.com">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base">
                <Mail className="w-4 h-4 mr-2" />
                hi@snigdha360.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 px-6 bg-[#0f1611]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p className="text-sm text-emerald-400 uppercase tracking-widest mb-4 font-medium">Contact</p>
              <h2 className="text-3xl md:text-4xl font-light mb-8 text-white">
                Let's discuss your<br />
                <span className="text-slate-400">requirements</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-[#152219] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Email</p>
                    <a href="mailto:hi@snigdha360.com" className="text-white hover:text-emerald-400 transition-colors">
                      hi@snigdha360.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">India</p>
                    <a href="tel:+917570041003" className="text-white hover:text-emerald-400 transition-colors">
                      +91 757 004 1003
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">United States</p>
                    <a href="tel:+19295520247" className="text-white hover:text-emerald-400 transition-colors">
                      +1 (929) 552-0247
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-emerald-400 uppercase tracking-widest mb-4 font-medium">Offices</p>
              <div className="space-y-8">
                {/* US Office */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium">United States</p>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">US</span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      418 Broadway #5781,<br />
                      Albany, New York, 12207,<br />
                      United States
                    </p>
                  </div>
                </div>
                {/* Gwalior Office */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium">Gwalior</p>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">India</span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      103, First Floor, Vindhyachal Complex,<br />
                      Opp Krishna Business Tower, City Center,<br />
                      Gwalior, MP 474002
                    </p>
                  </div>
                </div>
                {/* Indore Office */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium">Indore</p>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">India</span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      4th Floor, Shalimar Town,<br />
                      392/3 and 391/17, Vijay Nagar,<br />
                      Indore, MP 452010
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-emerald-900/40 py-12 px-6 bg-[#0b1310]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Snigdha & Associates" 
                className="w-8 h-8 object-contain"
              />
              <div>
                <p className="text-sm text-slate-700">Snigdha & Associates</p>
                <p className="text-xs text-slate-400">Chartered Accountants</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <Link to="/mp-startup-benefits" data-testid="footer-mp-startup-benefits-link" className="text-sm text-slate-400 hover:text-white transition-colors">MP Startup Funding & Benefits</Link>
              <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy</Link>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-500/100/20">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-emerald-400">ISO 27001</span>
              </div>
              <p className="text-xs text-slate-400">© 2026 All rights reserved</p>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPagePro;

